import { Camera, CheckCircle2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Spinner } from '@/components/ui/states';
import { cn } from '@/lib/utils';
import { uploadFleetPhoto, useFleetPhotoSrc, type PhotoKind } from './fuel-api';

/** Ô chụp ảnh trên điện thoại: mở camera sau, thu nhỏ, gửi ngay, trả về id ảnh. Ảnh dùng rồi không dùng lại được (server chặn). */
export function PhotoField({
  label,
  kind,
  value,
  onChange,
  required,
  error,
  upload,
}: {
  label: string;
  kind: PhotoKind;
  /** PR5: ảnh checklist / phiếu cân / qua đêm gửi qua /fleet/driver-rules/photos */
  upload?: (file: File) => Promise<{ id: number }>;
  value: number | null;
  onChange: (id: number | null) => void;
  required?: boolean;
  error?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const src = useFleetPhotoSrc(value);
  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const { id } = await (upload ? upload(file) : uploadFleetPhoto(kind, file));
      onChange(id);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  };
  return (
    <div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className={cn(
          'flex w-full items-center gap-3 rounded-md border-2 border-dashed p-2 text-left text-sm',
          value ? 'border-success bg-success-soft/40' : error ? 'border-destructive' : 'border-foreground/30',
        )}
      >
        {src ? <img src={src} alt={label} className="size-14 shrink-0 rounded object-cover" /> : <span className="flex size-14 shrink-0 items-center justify-center rounded bg-primary/10">{busy ? <Spinner /> : <Camera className="size-6 text-primary" />}</span>}
        <span className="min-w-0 flex-1">
          <span className="block font-medium">
            {label}
            {required && <span className="text-destructive"> *</span>}
          </span>
          <span className="block text-xs text-muted-foreground">{value ? 'Đã chụp. Bấm để chụp lại.' : 'Bấm để chụp bằng camera'}</span>
        </span>
        {value && <CheckCircle2 className="size-5 shrink-0 text-success" />}
      </button>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void pick(e.target.files?.[0])} />
    </div>
  );
}

/** Ảnh nhỏ bấm mở ảnh lớn (màn quản lý). */
export function PhotoThumb({ id, label }: { id: number | null; label: string }) {
  const src = useFleetPhotoSrc(id);
  const [open, setOpen] = useState(false);
  const full = useFleetPhotoSrc(open ? id : null, 'full');
  if (!id) return <span className="text-xs text-muted-foreground">Không có {label.toLowerCase()}</span>;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="block text-left">
        {src ? <img src={src} alt={label} className="h-28 w-full rounded border object-cover" /> : <span className="flex h-28 items-center justify-center rounded border"><Spinner /></span>}
        <span className="mt-0.5 block text-xs text-muted-foreground">{label}</span>
      </button>
      {open && (
        <button type="button" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setOpen(false)} aria-label="Đóng ảnh">
          {full ? <img src={full} alt={label} className="max-h-full max-w-full object-contain" /> : <Spinner className="text-white" />}
        </button>
      )}
    </>
  );
}
